function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { forwardRef, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { isEqual } from 'lodash-es';
import { startOfDay, subMonths } from 'date-fns';
import { DropdownMenu, FilterPicker, MultiSelectFilterPicker, RadioButtonWithLabel, TextButton, Typography, KeyboardDatePicker } from 'seagull';
import { selectors } from "../../store/state-selectors";
import { relationshipSummaryModel, timeModel } from "../../models";
import { useIoContext, useStoreActions, useStoreStatic } from "../../contexts";
import styles from "./agreementsDashboardFilters.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export let FilterType = /*#__PURE__*/function (FilterType) {
  FilterType["AgreementStatus"] = "agreementStatus";
  FilterType["EffectiveDate"] = "effectiveDate";
  FilterType["LastBillingDate"] = "lastBillingDate";
  FilterType["PaymentMethod"] = "paymentMethod";
  FilterType["HasCredit"] = "hasCredit";
  FilterType["Completion"] = "completion";
  FilterType["BillingTrigger"] = "billingTrigger";
  FilterType["Recurrence"] = "recurrence";
  FilterType["Automatic"] = "automatic";
  FilterType["ServiceNames"] = "serviceNames";
  FilterType["ServicePriceIncreaseState"] = "servicePriceIncreaseState";
  FilterType["HasAmendments"] = "hasAmendments";
  return FilterType;
}({});
export let PriceIncreaseFilterValue = /*#__PURE__*/function (PriceIncreaseFilterValue) {
  PriceIncreaseFilterValue["None"] = "none";
  PriceIncreaseFilterValue["Automatic"] = "automatic";
  return PriceIncreaseFilterValue;
}({});
export const defaultAgreementStatusFilter = [relationshipSummaryModel.AgreementStatus.Active, relationshipSummaryModel.AgreementStatus.Exhausted];
export function deserializeFiltersFromURL(urlParams) {
  return {
    [FilterType.AgreementStatus]: deserializeFilter(FilterType.AgreementStatus, urlParams.agreementStatus),
    [FilterType.EffectiveDate]: deserializeFilter(FilterType.EffectiveDate, urlParams.effectiveDate),
    [FilterType.LastBillingDate]: deserializeFilter(FilterType.LastBillingDate, urlParams.lastBillingDate),
    [FilterType.PaymentMethod]: deserializeFilter(FilterType.PaymentMethod, urlParams.paymentMethod),
    [FilterType.Completion]: deserializeFilter(FilterType.Completion, urlParams.completion),
    [FilterType.Recurrence]: deserializeFilter(FilterType.Recurrence, urlParams.recurrence),
    [FilterType.HasCredit]: deserializeFilter(FilterType.HasCredit, urlParams.hasCredit),
    [FilterType.Automatic]: deserializeFilter(FilterType.Automatic, urlParams.automatic),
    [FilterType.BillingTrigger]: deserializeFilter(FilterType.BillingTrigger, urlParams.billingTrigger),
    [FilterType.ServiceNames]: deserializeFilter(FilterType.ServiceNames, urlParams.serviceNames),
    [FilterType.ServicePriceIncreaseState]: deserializeFilter(FilterType.ServicePriceIncreaseState, urlParams.servicePriceIncreaseState),
    [FilterType.HasAmendments]: deserializeFilter(FilterType.HasAmendments, urlParams.hasAmendments)
  };
  function deserializeFilter(type, value) {
    const {
      deserialize
    } = filtersConfig[type];
    if (typeof value !== 'string') return value;
    return deserialize ? deserialize(value) : value;
  }
}
const agreementStatusLabel = 'Agreement status';
const effectiveDateLabel = 'Effective date';
const lastBillingDateLabel = 'Last billing date';
const paymentMethodLabel = 'Payment method';
const completionLabel = 'Service status';
const recurrenceLabel = 'Occurrence';
const hasCreditLabel = 'Has credit';
const automaticLabel = 'Automatic services state';
const billingTriggerLabel = 'Billing trigger';
const serviceNameLabel = 'Service name';
const priceIncreaseLabel = 'Automatic price increase';
const hasAmendmentsLabel = 'Is amended';
const FilterSelectorMenuTitle = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(Typography, {
  ref: ref,
  className: cn(styles.menuTitle, props.className),
  variant: "paragraph2",
  weight: "normal",
  skin: "secondary",
  children: props.children
}));
export function FilterSelector(props) {
  const {
    selected,
    onSelect
  } = props;
  const {
    agreementItems,
    serviceItems
  } = useMemo(() => {
    const getEntry = (type, text) => ({
      type,
      text,
      disabled: selected.includes(type)
    });
    const agreementItems = [getEntry(FilterType.AgreementStatus, agreementStatusLabel), getEntry(FilterType.EffectiveDate, effectiveDateLabel), getEntry(FilterType.LastBillingDate, lastBillingDateLabel), getEntry(FilterType.PaymentMethod, paymentMethodLabel), getEntry(FilterType.HasCredit, hasCreditLabel), getEntry(FilterType.ServicePriceIncreaseState, priceIncreaseLabel), getEntry(FilterType.HasAmendments, hasAmendmentsLabel)];
    const serviceItems = [getEntry(FilterType.ServiceNames, serviceNameLabel), getEntry(FilterType.Completion, completionLabel), getEntry(FilterType.BillingTrigger, billingTriggerLabel), getEntry(FilterType.Recurrence, recurrenceLabel), getEntry(FilterType.Automatic, automaticLabel)];
    return {
      agreementItems,
      serviceItems
    };
  }, [selected]);
  const hasMoreToSelect = selected.length < agreementItems.length + serviceItems.length;
  function getMenuItem(type, text, disabled) {
    return /*#__PURE__*/_jsx(DropdownMenu.Item, {
      skin: "primary",
      "data-testid": "filter-option-".concat(type),
      disabled: disabled,
      onClick: () => onSelect(type),
      children: text
    }, type);
  }
  return hasMoreToSelect ? /*#__PURE__*/_jsxs(DropdownMenu, {
    hAlign: "center",
    target: /*#__PURE__*/_jsx(TextButton, {
      "data-testid": "add-filter",
      underline: "hover",
      children: "+Add filter"
    }),
    children: [/*#__PURE__*/_jsx(FilterSelectorMenuTitle, {
      children: "Agreements"
    }), agreementItems.map(_ref => {
      let {
        type,
        text,
        disabled
      } = _ref;
      return getMenuItem(type, text, disabled);
    }), /*#__PURE__*/_jsx(FilterSelectorMenuTitle, {
      className: styles.marginTop,
      children: "Services"
    }), serviceItems.map(_ref2 => {
      let {
        type,
        text,
        disabled
      } = _ref2;
      return getMenuItem(type, text, disabled);
    })]
  }) : null;
}
const agreementStatusFilterOptions = [{
  value: relationshipSummaryModel.AgreementStatus.Active,
  label: 'Active'
}, {
  value: relationshipSummaryModel.AgreementStatus.Exhausted,
  label: 'Completed'
}, {
  value: relationshipSummaryModel.AgreementStatus.Terminated,
  label: 'Terminated'
}];
function AgreementStatusFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
    removable: true,
    startOpen: props.startOpen,
    "data-testid": "agreement-status-filter",
    value: props.value,
    options: agreementStatusFilterOptions,
    label: agreementStatusLabel,
    onChange: props.onChange,
    onRemove: props.onRemove
  });
}
export let HasAmendmentsFilterValue = /*#__PURE__*/function (HasAmendmentsFilterValue) {
  HasAmendmentsFilterValue["True"] = "true";
  HasAmendmentsFilterValue["False"] = "false";
  return HasAmendmentsFilterValue;
}({});
const hasAmendmentsFilterOptions = [{
  value: HasAmendmentsFilterValue.True,
  label: 'Yes'
}, {
  value: HasAmendmentsFilterValue.False,
  label: 'No'
}];
function HasAmendmentsFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
    removable: true,
    startOpen: props.startOpen,
    "data-testid": "has-amendments-filter",
    value: props.value,
    options: hasAmendmentsFilterOptions,
    label: hasAmendmentsLabel,
    onChange: value => props.onChange(value.length > 1 ? [] : value),
    onRemove: props.onRemove
  });
}
function EffectiveDateFilter(props) {
  const {
    value,
    startOpen,
    onChange,
    onRemove,
    onInputBlur,
    onInputFocus
  } = props;
  const {
    getNow
  } = useIoContext();
  const hasDatesFromProps = !!(value.fromDate && value.toDate);
  const [selectingRange, setSelectingRange] = useState(hasDatesFromProps);
  const [fromDate, setFromDate] = useState(getInitialFromDate);
  const [toDate, setToDate] = useState(getInitialToDate);
  useEffect(() => {
    value.fromDate && setFromDate(value.fromDate);
  }, [value.fromDate]);
  useEffect(() => {
    value.toDate && setToDate(value.toDate);
  }, [value.toDate]);
  const label = useMemo(() => {
    if (value.fromDate && value.toDate) {
      return "".concat(effectiveDateLabel, " (").concat(timeModel.formatDate(value.fromDate), "-").concat(timeModel.formatDate(value.toDate), ")");
    }
    return effectiveDateLabel;
  }, [value.fromDate, value.toDate]);
  function onChangeIfDifferentValue(newValue) {
    if (!isEqual(newValue, value)) {
      onChange(newValue);
    }
  }
  function getInitialFromDate() {
    return value.fromDate || subMonths(getNow(), 1);
  }
  function getInitialToDate() {
    return value.toDate || getNow();
  }
  return /*#__PURE__*/_jsxs(FilterPicker, {
    removable: true,
    startOpen: startOpen,
    "data-testid": "effective-date-filter",
    disableApply: selectingRange && fromDate && toDate && fromDate.getTime() > toDate.getTime(),
    label: label,
    title: effectiveDateLabel,
    highlightTrigger: !!(value.fromDate && value.toDate),
    onRemove: onRemove,
    onApply: () => onChangeIfDifferentValue(selectingRange ? {
      fromDate,
      toDate
    } : {}),
    onClose: opts => {
      if (opts !== null && opts !== void 0 && opts.revert) {
        setSelectingRange(hasDatesFromProps);
        setFromDate(getInitialFromDate());
        setToDate(getInitialToDate());
      }
    },
    children: [/*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: "Select all",
      testId: "effective-date-filter-all",
      checked: !selectingRange,
      onChange: () => setSelectingRange(false)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: "Specific range",
      testId: "effective-date-filter-range",
      checked: selectingRange,
      onChange: () => {
        setSelectingRange(true);
        const now = startOfDay(getNow());
        if (!fromDate) {
          setFromDate(now);
        }
        if (!toDate) {
          setToDate(now);
        }
      }
    }), selectingRange && fromDate && toDate && /*#__PURE__*/_jsx(DateRangePicker, {
      testid: "effective-date-filter",
      fromDate: fromDate,
      toDate: toDate,
      onChange: data => {
        setFromDate(data.fromDate);
        setToDate(data.toDate);
      },
      onInputFocus: onInputFocus,
      onInputBlur: onInputBlur
    })]
  });
}
export let LastBillingDateType = /*#__PURE__*/function (LastBillingDateType) {
  LastBillingDateType["None"] = "none";
  LastBillingDateType["Never"] = "never";
  LastBillingDateType["Ever"] = "ever";
  LastBillingDateType["Range"] = "range";
  return LastBillingDateType;
}({});
function getLastBillingDateOptionLabel(type) {
  switch (type) {
    case LastBillingDateType.Ever:
      return 'Ever billed';
    case LastBillingDateType.Never:
      return 'Never billed';
    case LastBillingDateType.Range:
      return 'Specific range';
    case LastBillingDateType.None:
      return '';
  }
}
function LastBillingDateFilter(props) {
  const {
    value,
    startOpen,
    onChange,
    onRemove,
    onInputBlur,
    onInputFocus
  } = props;
  const {
    getNow
  } = useIoContext();
  const [type, setType] = useState(value.type);
  const [fromDate, setFromDate] = useState(getInitialFromDate);
  const [toDate, setToDate] = useState(getInitialToDate);
  const disableApply = fromDate && toDate && fromDate.getTime() > toDate.getTime();
  useEffect(() => {
    setType(value.type);
  }, [value.type]);
  useEffect(() => {
    value.fromDate && setFromDate(value.fromDate);
  }, [value.fromDate]);
  useEffect(() => {
    value.toDate && setToDate(value.toDate);
  }, [value.toDate]);
  const label = useMemo(() => {
    switch (value.type) {
      case LastBillingDateType.Ever:
      case LastBillingDateType.Never:
        return "".concat(lastBillingDateLabel, " (").concat(getLastBillingDateOptionLabel(value.type), ")");
      case LastBillingDateType.Range:
        return "".concat(lastBillingDateLabel, " (").concat(timeModel.formatDate(value.fromDate), "-").concat(timeModel.formatDate(value.toDate), ")");
      case LastBillingDateType.None:
        return lastBillingDateLabel;
    }
  }, [value]);
  function onChangeIfDifferentValue(newValue) {
    if (!isEqual(newValue, value)) {
      onChange(newValue);
    }
  }
  function getInitialFromDate() {
    return value.fromDate || subMonths(getNow(), 1);
  }
  function getInitialToDate() {
    return value.toDate || getNow();
  }
  return /*#__PURE__*/_jsxs(FilterPicker, {
    removable: true,
    startOpen: startOpen,
    "data-testid": "last-billing-date-filter",
    disableApply: disableApply,
    label: label,
    title: lastBillingDateLabel,
    highlightTrigger: value.type !== LastBillingDateType.None,
    onRemove: onRemove,
    onApply: () => {
      if (type === LastBillingDateType.Range) {
        fromDate && toDate && onChangeIfDifferentValue({
          type,
          fromDate,
          toDate
        });
      } else {
        onChangeIfDifferentValue({
          type
        });
      }
    },
    onClose: opts => {
      if (opts !== null && opts !== void 0 && opts.revert) {
        setType(value.type);
        setFromDate(getInitialFromDate());
        setToDate(getInitialToDate());
      }
    },
    children: [/*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: "Select all",
      testId: "last-billing-date-filter-all",
      checked: type === LastBillingDateType.None,
      onChange: () => setType(LastBillingDateType.None)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: getLastBillingDateOptionLabel(LastBillingDateType.Ever),
      testId: "last-billing-date-filter-ever",
      checked: type === LastBillingDateType.Ever,
      onChange: () => setType(LastBillingDateType.Ever)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: getLastBillingDateOptionLabel(LastBillingDateType.Never),
      testId: "last-billing-date-filter-never",
      checked: type === LastBillingDateType.Never,
      onChange: () => setType(LastBillingDateType.Never)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: getLastBillingDateOptionLabel(LastBillingDateType.Range),
      testId: "last-billing-date-filter-range",
      checked: type === LastBillingDateType.Range,
      onChange: () => {
        setType(LastBillingDateType.Range);
        const now = startOfDay(getNow());
        if (!fromDate) {
          setFromDate(now);
        }
        if (!toDate) {
          setToDate(now);
        }
      }
    }), type === LastBillingDateType.Range && fromDate && toDate && /*#__PURE__*/_jsx(DateRangePicker, {
      testid: "last-billing-date-filter",
      fromDate: fromDate,
      toDate: toDate,
      onChange: data => {
        setFromDate(data.fromDate);
        setToDate(data.toDate);
      },
      onInputFocus: onInputFocus,
      onInputBlur: onInputBlur
    })]
  });
}
export let PaymentMethodFilterValue = /*#__PURE__*/function (PaymentMethodFilterValue) {
  PaymentMethodFilterValue["CreditCard"] = "credit";
  PaymentMethodFilterValue["BankAccount"] = "bank";
  PaymentMethodFilterValue["VerificationInitiated"] = "verificationInitiated";
  PaymentMethodFilterValue["VerificationFailed"] = "verificationFailed";
  PaymentMethodFilterValue["NotSet"] = "notSet";
  return PaymentMethodFilterValue;
}({});
const paymentMethodFilterOptions = [{
  value: PaymentMethodFilterValue.CreditCard,
  label: 'Credit card'
}, {
  value: PaymentMethodFilterValue.BankAccount,
  label: 'Bank account'
}, {
  value: PaymentMethodFilterValue.VerificationInitiated,
  label: 'Manual verification initiated'
}, {
  value: PaymentMethodFilterValue.VerificationFailed,
  label: 'Manual verification failed'
}, {
  value: PaymentMethodFilterValue.NotSet,
  label: 'Not set'
}];
function PaymentMethodFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
    removable: true,
    startOpen: props.startOpen,
    "data-testid": "payment-method-filter",
    value: props.value,
    options: paymentMethodFilterOptions,
    label: paymentMethodLabel,
    onChange: props.onChange,
    onRemove: props.onRemove
  });
}
export let CompletionFilterValue = /*#__PURE__*/function (CompletionFilterValue) {
  CompletionFilterValue["Completed"] = "completed";
  CompletionFilterValue["NotCompleted"] = "notCompleted";
  return CompletionFilterValue;
}({});
const completionFilterOptions = [{
  value: CompletionFilterValue.NotCompleted,
  label: 'Not completed'
}, {
  value: CompletionFilterValue.Completed,
  label: 'Completed'
}];
function CompletionFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, _objectSpread({
    removable: true,
    "data-testid": "completion-filter",
    options: completionFilterOptions,
    label: completionLabel
  }, props));
}
export let RecurrenceFilterValue = /*#__PURE__*/function (RecurrenceFilterValue) {
  RecurrenceFilterValue["OneTime"] = "oneTime";
  RecurrenceFilterValue["Weekly"] = "weekly";
  RecurrenceFilterValue["Biweekly"] = "biweekly";
  RecurrenceFilterValue["Monthly"] = "monthly";
  RecurrenceFilterValue["Quarterly"] = "quarterly";
  RecurrenceFilterValue["Yearly"] = "yearly";
  return RecurrenceFilterValue;
}({});
const recurrenceFilterOptions = [{
  value: RecurrenceFilterValue.OneTime,
  label: 'One time'
}, {
  value: RecurrenceFilterValue.Weekly,
  label: 'Weekly'
}, {
  value: RecurrenceFilterValue.Biweekly,
  label: 'Bi-weekly'
}, {
  value: RecurrenceFilterValue.Monthly,
  label: 'Monthly'
}, {
  value: RecurrenceFilterValue.Quarterly,
  label: 'Quarterly'
}, {
  value: RecurrenceFilterValue.Yearly,
  label: 'Yearly'
}];
function RecurrenceFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, _objectSpread({
    removable: true,
    "data-testid": "recurrence-filter",
    options: recurrenceFilterOptions,
    label: recurrenceLabel
  }, props));
}
export let HasCreditFilterValue = /*#__PURE__*/function (HasCreditFilterValue) {
  HasCreditFilterValue["Yes"] = "yes";
  HasCreditFilterValue["No"] = "no";
  return HasCreditFilterValue;
}({});
const hasCreditFilterOptions = [{
  value: HasCreditFilterValue.Yes,
  label: 'Yes'
}, {
  value: HasCreditFilterValue.No,
  label: 'No'
}];
function HasCreditFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, _objectSpread({
    removable: true,
    "data-testid": "has-credit-filter",
    options: hasCreditFilterOptions,
    label: hasCreditLabel
  }, props));
}
export let AutomaticFilterValue = /*#__PURE__*/function (AutomaticFilterValue) {
  AutomaticFilterValue["Active"] = "active";
  AutomaticFilterValue["Paused"] = "paused";
  return AutomaticFilterValue;
}({});
const automaticFilterOptions = [{
  value: AutomaticFilterValue.Active,
  label: 'Active'
}, {
  value: AutomaticFilterValue.Paused,
  label: 'Paused'
}];
function AutomaticFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, _objectSpread({
    removable: true,
    "data-testid": "automatic-filter",
    options: automaticFilterOptions,
    label: automaticLabel
  }, props));
}
export let BillingTriggerFilterValue = /*#__PURE__*/function (BillingTriggerFilterValue) {
  BillingTriggerFilterValue["Automatic"] = "automatic";
  BillingTriggerFilterValue["Manual"] = "manual";
  return BillingTriggerFilterValue;
}({});
const billingTriggerFilterOptions = [{
  value: BillingTriggerFilterValue.Automatic,
  label: 'Automatic'
}, {
  value: BillingTriggerFilterValue.Manual,
  label: 'Manual'
}];
function BillingTriggerFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, _objectSpread({
    removable: true,
    "data-testid": "billing-trigger-filter",
    options: billingTriggerFilterOptions,
    label: billingTriggerLabel
  }, props));
}
function DateRangePicker(props) {
  const {
    fromDate,
    toDate,
    testid,
    onChange,
    onInputBlur,
    onInputFocus
  } = props;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(KeyboardDatePicker, {
      label: "From",
      "data-testid": testid && "".concat(testid, "-from"),
      value: fromDate,
      maxDate: toDate,
      onChange: function (_onChange) {
        function onChange(_x) {
          return _onChange.apply(this, arguments);
        }
        onChange.toString = function () {
          return _onChange.toString();
        };
        return onChange;
      }(date => date && onChange && onChange({
        fromDate: date,
        toDate
      })),
      onFocus: onInputFocus,
      onBlur: onInputBlur
    }), /*#__PURE__*/_jsx(KeyboardDatePicker, {
      label: "To",
      "data-testid": testid && "".concat(testid, "-to"),
      value: toDate,
      minDate: fromDate,
      onChange: function (_onChange2) {
        function onChange(_x2) {
          return _onChange2.apply(this, arguments);
        }
        onChange.toString = function () {
          return _onChange2.toString();
        };
        return onChange;
      }(date => date && onChange && onChange({
        fromDate,
        toDate: date
      })),
      onFocus: onInputFocus,
      onBlur: onInputBlur
    })]
  });
}
function ServiceNamesFilter(props) {
  const {
    api
  } = useStoreActions();
  const {
    getState
  } = useStoreStatic();
  const [options, setOptions] = useState([]);
  useEffect(() => {
    const businessId = selectors.user.businessId(getState());
    const isVendor = selectors.user.isVendor(getState());
    if (businessId) {
      api.relationship.getServiceNames(businessId, isVendor, 'agreement').then(names => {
        setOptions(names.map(n => ({
          label: n,
          value: n
        })));
      });
    }
  }, []);
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, _objectSpread(_objectSpread({
    removable: true,
    withSearchbox: true,
    "data-testid": "service-names-filter",
    options: options,
    label: serviceNameLabel
  }, props), {}, {
    onInputFocus: props.onInputFocus,
    onInputBlur: props.onInputBlur
  }));
}
const priceIncreaseFilterOptions = [{
  value: PriceIncreaseFilterValue.Automatic,
  label: 'Included'
}, {
  value: PriceIncreaseFilterValue.None,
  label: 'Not included'
}];
function PriceIncreaseFilter(props) {
  return /*#__PURE__*/_jsx(MultiSelectFilterPicker, _objectSpread({
    removable: true,
    "data-testid": "price-increase-filter",
    options: priceIncreaseFilterOptions,
    label: priceIncreaseLabel
  }, props));
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type, component) {
  return {
    component,
    hasValue(v) {
      return v.length > 0;
    },
    getEmptyValue() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [];
    },
    getRequestParam(v) {
      return {
        [type]: v.length ? v.join(',') : undefined
      };
    }
  };
}
const effectiveDateConfig = {
  component: EffectiveDateFilter,
  hasValue(effectiveDate) {
    return !!(effectiveDate.fromDate && effectiveDate.toDate);
  },
  getEmptyValue() {
    return {};
  },
  getRequestParam(effectiveDate) {
    return {
      fromDate: effectiveDate.fromDate && timeModel.toDatestampString(effectiveDate.fromDate),
      toDate: effectiveDate.toDate && timeModel.toDatestampString(effectiveDate.toDate)
    };
  },
  serialize(_ref3) {
    let {
      fromDate,
      toDate
    } = _ref3;
    return fromDate && toDate ? "".concat(timeModel.toDatestampString(fromDate), ",").concat(timeModel.toDatestampString(toDate)) : '';
  },
  deserialize(str) {
    const splitted = str.split(',');
    return splitted.length === 2 ? {
      fromDate: timeModel.fromDatestampString(splitted[0]),
      toDate: timeModel.fromDatestampString(splitted[1])
    } : {};
  }
};
const lastBillingDateConfig = {
  component: LastBillingDateFilter,
  hasValue(value) {
    return value.type !== LastBillingDateType.None;
  },
  getEmptyValue() {
    return {
      type: LastBillingDateType.None
    };
  },
  getRequestParam(lastBillingDate) {
    const isLastBilledWithinRange = lastBillingDate.type === LastBillingDateType.Range;
    return {
      lastBilled: lastBillingDate.type !== LastBillingDateType.None ? lastBillingDate.type : undefined,
      lastBilledFrom: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.fromDate) : undefined,
      lastBilledTo: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.toDate) : undefined
    };
  },
  serialize(data) {
    if (data.type === LastBillingDateType.None) {
      return '';
    }
    if (data.type === LastBillingDateType.Range) {
      return "".concat(timeModel.toDatestampString(data.fromDate), ",").concat(timeModel.toDatestampString(data.toDate));
    }
    return data.type;
  },
  deserialize(lastBilled) {
    if (!lastBilled) {
      return {
        type: LastBillingDateType.None
      };
    }
    if (lastBilled === LastBillingDateType.Ever || lastBilled === LastBillingDateType.Never) {
      return {
        type: lastBilled
      };
    }
    const dates = lastBilled.split(',');
    if (dates.length !== 2) {
      return {
        type: LastBillingDateType.None
      };
    }
    const fromDate = timeModel.fromDatestampString(dates[0]);
    const toDate = timeModel.fromDatestampString(dates[1]);
    return {
      type: LastBillingDateType.Range,
      fromDate,
      toDate
    };
  }
};
export const filtersConfig = {
  [FilterType.EffectiveDate]: effectiveDateConfig,
  [FilterType.LastBillingDate]: lastBillingDateConfig,
  [FilterType.AgreementStatus]: getSimpleMultiSelectFilterConfig(FilterType.AgreementStatus, AgreementStatusFilter),
  [FilterType.PaymentMethod]: getSimpleMultiSelectFilterConfig(FilterType.PaymentMethod, PaymentMethodFilter),
  [FilterType.Completion]: getSimpleMultiSelectFilterConfig(FilterType.Completion, CompletionFilter),
  [FilterType.Recurrence]: getSimpleMultiSelectFilterConfig(FilterType.Recurrence, RecurrenceFilter),
  [FilterType.HasCredit]: getSimpleMultiSelectFilterConfig(FilterType.HasCredit, HasCreditFilter),
  [FilterType.Automatic]: getSimpleMultiSelectFilterConfig(FilterType.Automatic, AutomaticFilter),
  [FilterType.BillingTrigger]: getSimpleMultiSelectFilterConfig(FilterType.BillingTrigger, BillingTriggerFilter),
  [FilterType.ServiceNames]: getSimpleMultiSelectFilterConfig(FilterType.ServiceNames, ServiceNamesFilter),
  [FilterType.ServicePriceIncreaseState]: getSimpleMultiSelectFilterConfig(FilterType.ServicePriceIncreaseState, PriceIncreaseFilter),
  [FilterType.HasAmendments]: getSimpleMultiSelectFilterConfig(FilterType.HasAmendments, HasAmendmentsFilter)
};
export function serializeFilter(type, value) {
  const {
    serialize
  } = filtersConfig[type];
  return serialize ? serialize(value) : value;
}